import { supabase } from '../lib/supabase';

/**
 * Admin Dashboard Service - REAL-TIME KPI & ANALYTICS
 * Fetches comprehensive statistics for ADT admin dashboard
 */
export const adminDashboardService = {
  /**
   * Get complete dashboard metrics (KPIs, charts, tables)
   */
  async getDashboardMetrics() {
    try {
      // Fetch all shipments with items for calculations
      const { data: shipments, error: shipmentsError } = await supabase?.from('shipments')?.select(`
          *,
          shipment_items(price_total)
        `)?.order('created_at', { ascending: false });

      if (shipmentsError) throw shipmentsError;

      // Fetch all containers
      const { data: containers, error: containersError } = await supabase?.from('containers')?.select('*')?.order('created_at', { ascending: false });

      if (containersError) throw containersError;

      // Fetch all clients
      const { data: clients, error: clientsError } = await supabase?.from('clients')?.select('*');

      if (clientsError) throw clientsError;

      // Calculate KPIs and metrics
      const metrics = this._calculateMetrics(shipments, containers, clients);

      return {
        success: true,
        data: metrics
      };
    } catch (error) {
      console.error('❌ Error fetching dashboard metrics:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des statistiques');
    }
  },

  /**
   * Private method to calculate all dashboard metrics
   */
  _calculateMetrics(shipments, containers, clients) {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    // Calculate totals for each shipment
    const shipmentsWithTotals = shipments?.map(shipment => {
      const items = shipment?.shipment_items || [];
      const itemsTotal = items?.reduce((sum, item) => sum + (parseFloat(item?.price_total) || 0), 0);
      const totalAmount = itemsTotal - (parseFloat(shipment?.discount_amount) || 0);
      const paidAmount = parseFloat(shipment?.paid_amount) || 0;
      const remainingAmount = Math.max(0, totalAmount - paidAmount);

      return {
        ...shipment,
        totalAmount,
        paidAmount,
        remainingAmount,
        createdAt: new Date(shipment?.created_at)
      };
    });

    // Filter shipments by month
    const shipmentsThisMonth = shipmentsWithTotals?.filter(s => 
      s?.createdAt >= firstDayOfMonth && s?.createdAt <= lastDayOfMonth
    );

    // ========== KPI CARDS (4 main metrics) ==========
    const kpis = {
      // KPI 1: CA encaissé (mois courant)
      revenueCollectedThisMonth: shipmentsThisMonth?.reduce((sum, s) => sum + s?.paidAmount, 0),

      // KPI 2: Reste à encaisser (global)
      remainingToCollect: shipmentsWithTotals?.reduce((sum, s) => sum + s?.remainingAmount, 0),

      // KPI 3: Envois du mois
      shipmentsThisMonth: shipmentsThisMonth?.length,

      // KPI 4: Conteneurs ouverts
      openContainers: containers?.filter(c => c?.status === 'draft' || c?.status === 'open')?.length
    };

    // ========== SHIPMENT STATUS DISTRIBUTION ==========
    const statusCounts = {
      pending: shipmentsWithTotals?.filter(s => s?.current_status === 'pending')?.length,
      in_transit: shipmentsWithTotals?.filter(s => s?.current_status === 'in_transit')?.length,
      customs: shipmentsWithTotals?.filter(s => s?.current_status === 'customs')?.length,
      out_for_delivery: shipmentsWithTotals?.filter(s => s?.current_status === 'out_for_delivery')?.length,
      delivered: shipmentsWithTotals?.filter(s => s?.current_status === 'delivered')?.length,
      exception: shipmentsWithTotals?.filter(s => s?.current_status === 'exception')?.length
    };

    // ========== CONTAINER STATUS BREAKDOWN ==========
    const containersByStatus = containers?.reduce((acc, container) => {
      const status = container?.status || 'draft';
      acc[status] = (acc?.[status] || 0) + 1;
      return acc;
    }, {});

    // ========== CONTAINERS TABLE DATA ==========
    const containersData = containers?.slice(0, 10)?.map(container => ({
      id: container?.id,
      code: container?.container_code,
      departureDate: container?.departure_date,
      departurePort: container?.departure_port,
      destinationPort: container?.destination_port,
      status: container?.status,
      totalShipments: container?.total_shipments || 0,
      vesselName: container?.vessel_name,
      shippingLine: container?.shipping_line
    }));

    // ========== TOP CLIENTS ANALYSIS ==========
    // Group shipments by sender_phone to calculate client metrics
    const clientMetrics = {};
    
    shipmentsWithTotals?.forEach(shipment => {
      const phone = shipment?.sender_phone;
      if (!phone) return;

      if (!clientMetrics?.[phone]) {
        clientMetrics[phone] = {
          phone,
          name: shipment?.sender_name,
          shipmentCount: 0,
          totalBilled: 0,
          totalPaid: 0
        };
      }

      clientMetrics[phone].shipmentCount++;
      clientMetrics[phone].totalBilled += shipment?.totalAmount;
      clientMetrics[phone].totalPaid += shipment?.paidAmount;
    });

    // Convert to array and sort by totalBilled (top 5)
    const topClients = Object.values(clientMetrics)?.sort((a, b) => b?.totalBilled - a?.totalBilled)?.slice(0, 5)?.map(client => {
        // Find client in clients table to get loyalty points
        const clientData = clients?.find(c => c?.phone === client?.phone);
        return {
          ...client,
          loyaltyPoints: clientData?.loyalty_points_total || 0
        };
      });

    // ========== LATEST SHIPMENTS (10 most recent) ==========
    const latestShipments = shipmentsWithTotals?.sort((a, b) => b?.createdAt - a?.createdAt)?.slice(0, 10)?.map(shipment => ({
        id: shipment?.id,
        reference: shipment?.reference,
        createdAt: shipment?.created_at,
        senderName: shipment?.sender_name,
        receiverName: shipment?.receiver_name,
        status: shipment?.current_status,
        paymentStatus: shipment?.payment_status,
        totalAmount: shipment?.totalAmount,
        paidAmount: shipment?.paidAmount
      }));

    return {
      kpis,
      statusCounts,
      containersByStatus,
      containersData,
      topClients,
      latestShipments
    };
  },

  /**
   * Get delivery tasks with INCIDENT status for admin monitoring
   * @param {Object} filters - Optional filters { status, dateFrom, dateTo, agentId }
   * @returns {Promise<Array>} Array of incident delivery tasks with agent details
   */
  async getIncidentDeliveryTasks(filters = {}) {
    try {
      let query = supabase?.from('delivery_tasks')?.select(`
          *,
          assigned_employee:employees!assigned_employee_id(
            id,
            name,
            email,
            phone
          ),
          shipment:shipments(
            reference,
            sender_name,
            receiver_name
          )
        `)?.eq('status', 'INCIDENT')?.order('updated_at', { ascending: false });

      // Apply optional filters
      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      if (filters?.agentId) {
        query = query?.eq('assigned_employee_id', filters?.agentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data for admin display
      return data?.map(task => ({
        id: task?.id,
        recipientName: task?.recipient_name,
        recipientPhone: task?.recipient_phone,
        deliveryAddress: task?.delivery_address,
        incidentType: task?.incident_type,
        incidentComment: task?.incident_comment,
        plannedDate: task?.planned_date,
        plannedTimeWindow: task?.planned_time_window,
        createdAt: task?.created_at,
        updatedAt: task?.updated_at,
        agent: {
          id: task?.assigned_employee?.id,
          name: task?.assigned_employee?.name,
          email: task?.assigned_employee?.email,
          phone: task?.assigned_employee?.phone
        },
        shipment: {
          reference: task?.shipment?.reference,
          senderName: task?.shipment?.sender_name,
          receiverName: task?.shipment?.receiver_name
        }
      })) || [];
    } catch (error) {
      console.error('❌ Error fetching incident delivery tasks:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des incidents de livraison');
    }
  },

  /**
   * Get all agents (employees) for filtering
   * @returns {Promise<Array>} Array of agents with id and name
   */
  async getAgentsForFilter() {
    try {
      const { data, error } = await supabase?.from('employees')?.select('id, name, email')?.eq('role', 'AGENT_ABJ')?.eq('is_active', true)?.order('name', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('❌ Error fetching agents:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des agents');
    }
  },

  /**
   * Resolve incident - revert delivery task from INCIDENT to A_PREPARER
   * Only admins can perform this action
   */
  resolveIncident: async (deliveryTaskId) => {
    const { data, error } = await supabase?.from('delivery_tasks')?.update({
        status: 'A_PREPARER',
        incident_type: null,
        incident_comment: null,
        updated_at: new Date()?.toISOString()
      })?.eq('id', deliveryTaskId)?.select()?.single();

    if (error) {
      throw new Error(`Erreur lors de la résolution de l'incident: ${error?.message}`);
    }

    return data;
  },

  /**
   * Reassign delivery task to another agent
   * Only admins can perform this action
   */
  reassignDeliveryTask: async (deliveryTaskId, newAgentId) => {
    const { data, error } = await supabase?.from('delivery_tasks')?.update({
        assigned_employee_id: newAgentId,
        updated_at: new Date()?.toISOString()
      })?.eq('id', deliveryTaskId)?.select()?.single();

    if (error) {
      throw new Error(`Erreur lors de la réassignation: ${error?.message}`);
    }

    return data;
  },

  /**
   * Get Abidjan containers with delivery statistics
   * For admin Distribution Abidjan dashboard
   * @param {Object} filters - Optional filters { status, dateFrom, dateTo }
   * @returns {Promise<Array>} Array of containers with delivery_tasks stats
   */
  async getAbidjanContainers(filters = {}) {
    try {
      // Build query for containers
      let containerQuery = supabase
        ?.from('containers')
        ?.select(`
          id,
          container_code,
          status,
          departure_date,
          eta_date,
          total_shipments,
          vessel_name,
          shipping_line,
          created_at,
          updated_at
        `)
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        containerQuery = containerQuery?.eq('status', filters?.status);
      }

      if (filters?.dateFrom) {
        containerQuery = containerQuery?.gte('departure_date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        containerQuery = containerQuery?.lte('departure_date', filters?.dateTo);
      }

      const { data: containers, error: containerError } = await containerQuery;

      if (containerError) throw containerError;

      // For each container, fetch delivery_tasks statistics
      const containersWithStats = await Promise.all(
        containers?.map(async (container) => {
          // Get all delivery tasks for this container
          const { data: deliveryTasks, error: tasksError } = await supabase
            ?.from('delivery_tasks')
            ?.select('id, status')
            ?.eq('container_id', container?.id);

          if (tasksError) {
            console.error(`Error fetching tasks for container ${container?.id}:`, tasksError);
            return {
              ...container,
              totalDeliveries: 0,
              deliveredCount: 0,
              incidentCount: 0
            };
          }

          // Calculate statistics
          const totalDeliveries = deliveryTasks?.length || 0;
          const deliveredCount = deliveryTasks?.filter(t => t?.status === 'LIVRE')?.length || 0;
          const incidentCount = deliveryTasks?.filter(t => t?.status === 'INCIDENT')?.length || 0;

          return {
            id: container?.id,
            containerCode: container?.container_code,
            status: container?.status,
            departureDate: container?.departure_date,
            etaDate: container?.eta_date,
            totalShipments: container?.total_shipments,
            vesselName: container?.vessel_name,
            shippingLine: container?.shipping_line,
            createdAt: container?.created_at,
            updatedAt: container?.updated_at,
            // Delivery statistics
            totalDeliveries,
            deliveredCount,
            incidentCount,
            // Calculate alert status
            hasIncidents: incidentCount > 0
          };
        })
      );

      return containersWithStats;
    } catch (error) {
      console.error('❌ Error fetching Abidjan containers:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des conteneurs Abidjan');
    }
  },

  /**
   * Get delivery tasks for a specific container
   * Used in container detail view
   * @param {string} containerId - UUID of the container
   * @returns {Promise<Array>} Array of delivery tasks with full details
   */
  async getContainerDeliveryTasks(containerId) {
    try {
      const { data, error } = await supabase
        ?.from('delivery_tasks')
        ?.select(`
          *,
          assigned_employee:employees!assigned_employee_id(
            id,
            name,
            email,
            phone,
            zone
          ),
          shipment:shipments(
            reference,
            sender_name,
            receiver_name,
            receiver_phone
          )
        `)
        ?.eq('container_id', containerId)
        ?.order('delivery_zone', { ascending: true })
        ?.order('route_order', { ascending: true });

      if (error) throw error;

      // Transform data for display
      return data?.map(task => ({
        id: task?.id,
        status: task?.status,
        recipientName: task?.recipient_name,
        recipientPhone: task?.recipient_phone,
        deliveryAddress: task?.delivery_address,
        deliveryZone: task?.delivery_zone,
        routeOrder: task?.route_order,
        plannedDate: task?.planned_date,
        plannedTimeWindow: task?.planned_time_window,
        deliveredAt: task?.delivered_at,
        incidentType: task?.incident_type,
        incidentComment: task?.incident_comment,
        deliveryNotes: task?.delivery_notes,
        agent: task?.assigned_employee ? {
          id: task?.assigned_employee?.id,
          name: task?.assigned_employee?.name,
          email: task?.assigned_employee?.email,
          phone: task?.assigned_employee?.phone,
          zone: task?.assigned_employee?.zone
        } : null,
        shipment: task?.shipment ? {
          reference: task?.shipment?.reference,
          senderName: task?.shipment?.sender_name,
          receiverName: task?.shipment?.receiver_name,
          receiverPhone: task?.shipment?.receiver_phone
        } : null,
        createdAt: task?.created_at,
        updatedAt: task?.updated_at
      })) || [];
    } catch (error) {
      console.error('❌ Error fetching container delivery tasks:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des livraisons du conteneur');
    }
  }
};